/**
 * Shared styles for message-composer dropdown selectors (agent + instance):
 * trigger, chip, floating menu panel, and menu list rows.
 */
import { spacing, Text } from '@scality/core-ui';
import styled, { css } from 'styled-components';

/** Mirrors `FocusVisibleStyle` from core-ui `Buttonv2` (dashed outline, not a heavy border). */
const focusVisibleStyleCoreUi = css`
	outline: dashed ${spacing.r2} ${(props) => props.theme.selectedActive};
	outline-offset: ${spacing.r2};
	z-index: 1000;
`;

/** Match core-ui buttons: keyboard-only dashed outline, not the browser default ring. */
const focusResetAndCoreUiVisible = css`
	&:focus {
		outline: none;
	}
	&:focus-visible:not(:disabled) {
		${focusVisibleStyleCoreUi}
	}
`;

/* --- Shell --- */

export const SelectWrapper = styled.div<{ $truncate?: boolean }>`
	position: relative;
	display: inline-block;
	max-width: 100%;
	${({ $truncate }) =>
    $truncate
      ? css`
				flex-shrink: 1;
				min-width: 0;
			`
      : css`
				flex-shrink: 0;
			`}
`;

/* --- Trigger & chip --- */

/** Toggle when no value selected — transparent border matches SelectChip outer box (avoids ~2px height jump). */
export const SelectTriggerButton = styled.button<{ $isOpen?: boolean }>`
	display: inline-flex;
	align-items: center;
	gap: ${spacing.r8};
	box-sizing: border-box;
	min-height: 0;
	padding: 0 ${spacing.r12} 0 ${spacing.r8};
	border: 1px solid transparent;
	border-radius: ${spacing.r16};
	background-color: transparent;
	cursor: pointer;
	font: inherit;
	color: inherit;
	white-space: nowrap;
	min-width: 0;
	color: ${({ theme }) => theme.textSecondary};

	> svg {
		flex-shrink: 0;
	}
	${({ $isOpen }) =>
    $isOpen &&
    css`
		background-color: ${({ theme }) => theme.highlight};
	`}
	&:hover {
		background-color: ${({ theme }) => theme.highlight};
	}
	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
		background-color: ${({ theme }) => theme.backgroundLevel2};
	}
	${focusResetAndCoreUiVisible}
`;

export const SelectChip = styled.span`
	box-sizing: border-box;
	white-space: nowrap;
	display: inline-flex;
	flex-direction: row;
	align-items: center;
	gap: ${spacing.r8};
	min-width: 0;
	max-width: 100%;
	color: ${({ theme }) => theme.textSecondary};
	border: 1px solid ${({ theme }) => theme.border};
	border-radius: ${spacing.r16};
	padding-left: ${spacing.r12};
	padding-right: ${spacing.r8};
	background-color: ${({ theme }) => theme.highlight};

	${SelectTriggerButton} {
		border: none;
		padding-left: 0;
		padding-right: 0;
	}
`;

/** Chip label that truncates with an ellipsis when the chip is shrunk (e.g. narrow dual panel). */
export const SelectChipLabel = styled(Text)`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
`;

export const SelectClearButton = styled.button`
	background: none;
	border: none;
	cursor: pointer;
	padding: 0;
	margin: 0;
	display: flex;
	align-items: center;
	flex-shrink: 0;
	color: inherit;
	&:hover {
		color: ${({ theme }) => theme.textPrimary};
	}
	${focusResetAndCoreUiVisible}
`;

/* --- Field skin (the dashboard filter bar's look) --- */

/** Sized to content, the Deployment trigger swung 218–355px on the selection alone. */
export const SELECT_FIELD_WIDTH = '16rem';

/** The field skin's box, shared by both shapes below so they cannot drift apart. */
const selectFieldSurface = css<{ $isOpen?: boolean }>`
	/* A target width, not a floor: fixed, it overflowed a narrow panel instead of eliding. */
	width: ${SELECT_FIELD_WIDTH};
	max-width: 100%;
	min-width: 0;
	padding: ${spacing.r4} ${spacing.r8};
	border: 1px solid ${(p) => (p.$isOpen ? p.theme.selectedActive : p.theme.border)};
	border-radius: ${spacing.r4};
	background-color: ${(p) => p.theme.backgroundLevel1};
	color: ${(p) => p.theme.textPrimary};

	&:hover {
		background-color: ${(p) => p.theme.backgroundLevel2};
	}
`;

/** A field and its legend, side by side. Shrinkable, or the box's max-width resolves against nothing. */
export const SelectField = styled.div`
	display: inline-flex;
	align-items: center;
	gap: ${spacing.r8};
	min-width: 0;
	max-width: 100%;
`;

/** The legend, to the LEFT of the box. */
export const SelectFieldLabel = styled(Text)`
	flex-shrink: 0;
	white-space: nowrap;
`;

/** The whole box is the trigger — for a select with nothing to clear. */
export const SelectFieldTrigger = styled(SelectTriggerButton)`
	justify-content: space-between;
	${selectFieldSurface}
`;

/** Box holding a trigger *and* a clear button, which cannot nest inside the button it clears. */
export const SelectFieldBox = styled.span<{ $isOpen?: boolean; $disabled?: boolean }>`
	box-sizing: border-box;
	display: inline-flex;
	align-items: center;
	gap: ${spacing.r8};
	min-width: 0;
	max-width: 100%;
	white-space: nowrap;
	${selectFieldSurface}
	${(p) =>
    p.$disabled &&
    css`
			opacity: 0.5;
			cursor: not-allowed;
		`}

	${SelectTriggerButton} {
		flex: 1;
		min-width: 0;
		justify-content: space-between;
		border: none;
		padding: 0;
		color: inherit;

		/* The box owns the hover and the dimming, so the trigger inside stays flat. */
		&,
		&:hover,
		&:disabled {
			background-color: transparent;
			opacity: 1;
		}
	}
`;

/** The value inside a field box, clamped so a long one can't widen it. */
export const SelectFieldText = styled(Text)`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
`;

/* --- Floating menu (dropdown panel) --- */

/** Anchor + margin: open below trigger (welcome) vs above (chat composer). */
export const selectMenuPlacement = css<{ $openBelow: boolean }>`
	${(p) =>
    p.$openBelow
      ? `
		top: 100%;
		bottom: auto;
		margin: ${spacing.r8} 0 0 0;
	`
      : `
		bottom: 100%;
		top: auto;
		margin: 0 0 ${spacing.r8} 0;
	`}
`;

/** Visual shell only — position comes from Floating UI `floatingStyles` (`strategy: fixed`). */
export const selectMenuFloatingPanel = css`
	background-color: ${(p) => p.theme.backgroundLevel1};
	border: 1px solid ${(p) => p.theme.border};
	border-radius: 4px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	z-index: 10;
`;

/** Inner list reset — no bullets, flush to scroll container edges. */
export const SelectMenuPlainList = styled.ul`
	list-style: none;
	margin: 0;
	padding: 0;
`;

/** Shared menu row baseline; each selector adds flex layout and highlight color. */
export const selectMenuItemBase = css`
	padding: ${spacing.r8};
	cursor: pointer;
	border-radius: 4px;
`;
